import { FC, useCallback } from "react";
import { Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useEffectOnceWhen } from "rooks";
import { ExplorerSummaryAnomaliesFormattedDataType } from "@/store/resource-explorer/selectors/resource-explorer-summary/billingExplorerSummaryAnomaliesFormattedDataSelector";
import { ExplainWithAiAnomaliesTrends } from "@/components/pages/resource-explorer/components/explain-with-ai-panel/components/body/anomalies/ExplainWithAiAnomaliesTrends";
import { ExplainWithAiAnomaliesTypes } from "@/components/pages/resource-explorer/components/explain-with-ai-panel/components/utils/types";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { explainWithAiAnomalyTabSelector } from "@/store/explain-with-ai/selectors/slice-data/explainWithAiAnomalyTabSelector";
import { setExplainWithAiAnomalyTab } from "@/store/explain-with-ai/explainWithAiSlice";
import { AnomalyIcon } from "@/components/pages/resource-explorer/components/explain-with-ai-panel/components/body/anomalies/AnomalyIcon";

interface ExplainWithAiAnomaliesTrendsTabsProps {
  data?: ExplorerSummaryAnomaliesFormattedDataType;
}

export const ExplainWithAiAnomaliesTrendsTabs: FC<
  ExplainWithAiAnomaliesTrendsTabsProps
> = ({ data }) => {
  const dispatch = useAppDispatch();
  const tab = useAppSelector(explainWithAiAnomalyTabSelector);

  const tabChangeHandler = useCallback(
    (_: any, value: ExplainWithAiAnomaliesTypes) => {
      if (!tab || !value) {
        return;
      }
      dispatch(setExplainWithAiAnomalyTab(value));
    },
    [tab, dispatch],
  );

  useEffectOnceWhen(() => {
    if (tab && data?.[tab]?.length) {
      return;
    }
    const getInitialTabValue = (): ExplainWithAiAnomaliesTypes => {
      if (data?.increaseTrends?.length) return "increaseTrends";
      if (data?.decreaseTrends?.length) return "decreaseTrends";
      if (data?.spikes?.length) return "spikes";
      return "increaseTrends";
    };
    dispatch(setExplainWithAiAnomalyTab(getInitialTabValue()));
  }, !!data);

  return (
    <Stack spacing={1.5}>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={tab}
        sx={toggleButtonGroupSx}
        onChange={tabChangeHandler}
      >
        {data?.increaseTrends?.length && (
          <ToggleButton value="increaseTrends" sx={toggleButtonSx}>
            <AnomalyIcon
              type="increaseTrends"
              fontSize="small"
              sx={{
                mr: 0.5,
              }}
              color={tab === "increaseTrends" ? "primary" : "action"}
            />
            {data?.increaseTrends?.length} Increase
            {data?.increaseTrends?.length > 1 ? "s" : ""}
          </ToggleButton>
        )}
        {data?.decreaseTrends?.length && (
          <ToggleButton value="decreaseTrends" sx={toggleButtonSx}>
            <AnomalyIcon
              fontSize="small"
              type="decreaseTrends"
              sx={{
                mr: 0.5,
              }}
              color={tab === "decreaseTrends" ? "primary" : "action"}
            />
            {data?.decreaseTrends?.length} Decrease
            {data?.decreaseTrends?.length > 1 ? "s" : ""}
          </ToggleButton>
        )}
        {data?.spikes?.length && (
          <ToggleButton value="spikes" sx={toggleButtonSx}>
            <AnomalyIcon
              type="spikes"
              fontSize="small"
              sx={{
                mr: 0.5,
              }}
              color={tab === "spikes" ? "primary" : "action"}
            />
            {data?.spikes?.length} Spike{data?.spikes?.length > 1 ? "s" : ""}
          </ToggleButton>
        )}
      </ToggleButtonGroup>

      <ExplainWithAiAnomaliesTrends data={data} tab={tab} />
    </Stack>
  );
};

const toggleButtonSx = {
  px: 1.5,
  py: 0.5,
  flex: 1,
  "&.Mui-selected": {
    backgroundColor: "white",
    color: "primary.main",
  },
  "&.Mui-selected:hover": {
    backgroundColor: "white",
    color: "primary.main",
  },
  "&:hover": {
    backgroundColor: "transparent",
  },
  border: 0,
};

const toggleButtonGroupSx = {
  borderRadius: 1.5,
  bgcolor: "grey.300",
  p: 0.25,
  border: 0,

  "& .MuiToggleButton-root": {
    marginLeft: 0,
    borderRadius: 1,
  },
};
